import { useEffect, useRef, useState } from "react";
import { Volume2 } from "lucide-react";
import type HlsType from "hls.js";
import type { VideoTracker } from "@/lib/analytics/video-tracking";

interface VslPlayerProps {
  mediaId: string;
  masterUrl: string;
  posterUrl: string;
  className?: string;
}

/**
 * Muted-autoplay VSL video player. Renders only a poster `<img>` on the
 * server / initial paint (the LCP element) and defers hls.js, the video
 * element wiring, and analytics until after `load` and once the player
 * scrolls into view — see SPEC.md §2/§3.3/§6. Clicking the "tap for sound"
 * overlay restarts playback from 0 with audio, per the reference VSL UX.
 */
export function VslPlayer({ mediaId, masterUrl, posterUrl, className }: VslPlayerProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const hlsRef = useRef<HlsType | null>(null);
  const trackerRef = useRef<VideoTracker | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(true);

  useEffect(() => {
    const container = containerRef.current;
    const video = videoRef.current;
    if (!container || !video) return;

    let cancelled = false;
    let observer: IntersectionObserver | null = null;

    async function initPlayer(): Promise<void> {
      if (cancelled || !video) return;

      const [{ default: Hls }, { attachVideoTracking }] = await Promise.all([
        import("hls.js"),
        import("@/lib/analytics/video-tracking"),
      ]);

      if (cancelled) return;

      let hls: HlsType | null = null;
      if (Hls.isSupported()) {
        hls = new Hls();
        hls.loadSource(masterUrl);
        hls.attachMedia(video);
      } else if (video.canPlayType("application/vnd.apple.mpegurl")) {
        video.src = masterUrl;
      }
      hlsRef.current = hls;

      const tracker = attachVideoTracking(video, hls ?? null, { mediaId });
      trackerRef.current = tracker;

      video.muted = true;
      try {
        await video.play();
        if (!cancelled) {
          tracker.markAutoplay();
          setIsPlaying(true);
        }
      } catch {
        // Autoplay blocked by the browser; leave the overlay so the user
        // can start playback (and sound) explicitly.
      }
    }

    function runInitOnceInView(): void {
      observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting) {
              observer?.disconnect();
              void initPlayer();
              break;
            }
          }
        },
        { threshold: 0 },
      );
      if (container) observer.observe(container);
    }

    function handleLoad(): void {
      runInitOnceInView();
    }

    if (document.readyState === "complete") {
      runInitOnceInView();
      return () => {
        cancelled = true;
        observer?.disconnect();
        trackerRef.current?.destroy();
        hlsRef.current?.destroy();
      };
    }

    window.addEventListener("load", handleLoad, { once: true });

    return () => {
      cancelled = true;
      window.removeEventListener("load", handleLoad);
      observer?.disconnect();
      trackerRef.current?.destroy();
      hlsRef.current?.destroy();
    };
  }, [mediaId, masterUrl]);

  function handleTapForSound(): void {
    const video = videoRef.current;
    const tracker = trackerRef.current;
    if (!video) return;

    const from = video.currentTime;
    video.currentTime = 0;
    video.muted = false;
    void video.play();
    tracker?.markUnmute(from);
    setIsMuted(false);
    setIsPlaying(true);
  }

  return (
    <div ref={containerRef} className={`relative aspect-video w-full ${className ?? ""}`}>
      <img
        src={posterUrl}
        alt=""
        className="absolute inset-0 h-full w-full object-cover"
        fetchPriority="high"
      />
      <video
        ref={videoRef}
        muted
        playsInline
        preload="none"
        // Native controls (pause/play/scrub — scrub-to-0 restarts) appear once
        // the viewer turns sound on. During the muted-autoplay hook the overlay
        // owns the surface, so controls stay hidden there.
        controls={!isMuted}
        controlsList="nodownload"
        className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-500 ${
          isPlaying ? "opacity-100" : "opacity-0"
        }`}
      />
      {isMuted && (
        <button
          type="button"
          onClick={handleTapForSound}
          // The whole surface stays tappable — the badge is only the affordance.
          className="absolute inset-0 flex items-start justify-end p-3 sm:p-4"
        >
          <span className="flex items-center gap-2 rounded-full bg-white/10 py-2 pl-3 pr-4 backdrop-blur ring-1 ring-white/20">
            <Volume2 className="size-5 text-white" />
            <span className="text-[10px] uppercase tracking-[0.2em] text-white/70">
              Tap for sound
            </span>
          </span>
        </button>
      )}
    </div>
  );
}
